import { Helmet } from "react-helmet-async";

/**
 * SeoHead (V3.15) — Composant SEO unifié pour toutes les pages publiques AFRICTAL.
 *
 * Basé sur `react-helmet-async`, réutilise la logique historique du hook
 * `useSeo` d'EliteShowcase.js et l'étend à tout le site :
 *   • Title, description, canonical
 *   • Open Graph (og:title, og:description, og:image, og:url, og:type)
 *   • Twitter Card (summary_large_image)
 *   • JSON-LD (Schema.org) optionnel
 *   • Robots (par défaut : index,follow)
 *
 * Props :
 *   title        (string)   Titre de la page — sera concaténé "· AFRICTAL"
 *   description  (string)   Meta description, 140-160 caractères idéalement
 *   path         (string)   Chemin relatif (ex : /a-propos). Utilisé pour canonical + og:url.
 *   image        (string)   URL absolue ou relative de l'image de partage
 *   type         (string)   og:type — "website" (défaut) ou "profile", "article"...
 *   noIndex      (boolean)  Si true → robots="noindex,nofollow"
 *   jsonLd       (object)   Objet Schema.org à sérialiser
 *   lang         (string)   Langue de la page (fr par défaut)
 *   keywords     (string)   Meta keywords (optionnel, faible poids SEO)
 */
const DEFAULT_SITE = "AFRICTAL";
const DEFAULT_IMAGE = "/brand/africtal-logo.png";
const DEFAULT_DESCRIPTION =
  "AFRICTAL : la plateforme panafricaine qui connecte les talents, freelances, entreprises, entrepreneurs et investisseurs africains autour d'un écosystème digital certifié.";

function siteOrigin() {
  if (typeof window === "undefined") return "";
  return window.location.origin;
}

export default function SeoHead({
  title,
  description = DEFAULT_DESCRIPTION,
  path,
  image = DEFAULT_IMAGE,
  type = "website",
  noIndex = false,
  jsonLd,
  lang = "fr",
  keywords,
}) {
  const origin = siteOrigin();
  const url = path ? `${origin}${path}` : (typeof window !== "undefined" ? window.location.href : origin);
  const absImage = image?.startsWith("http") ? image : `${origin}${image || DEFAULT_IMAGE}`;
  const fullTitle = title ? `${title} · ${DEFAULT_SITE}` : `${DEFAULT_SITE} — Écosystème digital panafricain`;

  return (
    <Helmet prioritizeSeoTags>
      <html lang={lang} />
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <meta
        name="robots"
        content={noIndex ? "noindex,nofollow" : "index,follow,max-snippet:-1,max-image-preview:large"}
      />
      <link rel="canonical" href={url} />

      {/* Open Graph */}
      <meta property="og:site_name" content={DEFAULT_SITE} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={type} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={absImage} />
      <meta property="og:locale" content={lang === "fr" ? "fr_FR" : lang} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={absImage} />

      {/* JSON-LD */}
      {jsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(jsonLd)}
        </script>
      )}
    </Helmet>
  );
}
